import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { B2BLead, B2BLeadsFilters } from '../../modules/b2b/leads/types';

export interface B2BLeadsState {
  leads: B2BLead[];
  activeLeadId: string | null;
  activeLead: B2BLead | null;
  filters: B2BLeadsFilters;
  nextBeforeId: number | null;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingActiveLead: boolean;
  isUpdating: boolean;
  error: string | null;
}

const initialState: B2BLeadsState = {
  leads: [],
  activeLeadId: null,
  activeLead: null,
  filters: { query: '', status: 'all', mine: false },
  nextBeforeId: null,
  hasMore: true,
  isLoading: false,
  isLoadingActiveLead: false,
  isUpdating: false,
  error: null,
};

export const name = 'b2bLeads';

const b2bLeadsSlice = createSlice({
  name,
  initialState,
  reducers: {
    setLeads(state, { payload }: PayloadAction<B2BLead[]>) { state.leads = payload; },
    appendLeads(state, { payload }: PayloadAction<B2BLead[]>) {
      payload.forEach((lead) => {
        if (!state.leads.some(({ id }) => id === lead.id)) state.leads.push(lead);
      });
    },
    setActiveLeadId(state, { payload }: PayloadAction<string | null>) { state.activeLeadId = payload; },
    setActiveLead(state, { payload }: PayloadAction<B2BLead | null>) { state.activeLead = payload; },
    clearActiveLead(state) {
      state.activeLeadId = null;
      state.activeLead = null;
      state.isLoadingActiveLead = false;
    },
    setFilters(state, { payload }: PayloadAction<Partial<B2BLeadsFilters>>) {
      state.filters = { ...state.filters, ...payload };
    },
    resetPagination(state) {
      state.leads = [];
      state.nextBeforeId = null;
      state.hasMore = true;
      state.error = null;
    },
    setPagination(state, { payload }: PayloadAction<{ nextBeforeId: number | null; hasMore: boolean }>) {
      state.nextBeforeId = payload.nextBeforeId;
      state.hasMore = payload.hasMore;
    },
    setError(state, { payload }: PayloadAction<string | null>) { state.error = payload; },
    setIsLoading(state, { payload }: PayloadAction<boolean>) { state.isLoading = payload; },
    setIsLoadingActiveLead(state, { payload }: PayloadAction<boolean>) { state.isLoadingActiveLead = payload; },
    setIsUpdating(state, { payload }: PayloadAction<boolean>) { state.isUpdating = payload; },
  },
});

export const {
  appendLeads, clearActiveLead, resetPagination, setActiveLead,
  setActiveLeadId, setError, setFilters, setIsLoading,
  setIsLoadingActiveLead, setIsUpdating, setPagination, setLeads,
} = b2bLeadsSlice.actions;

export default b2bLeadsSlice.reducer;
